<template>
    <div
        class="filter-chip-container"
        :class="{ 'chip-container--removable': removable, 'chip-container--clearable': clearable }"
        :data-filter-field="dataFilterField"
    >
        <CustomPopover
            ref="customPopover"
            :icon="icon"
            :label="getLabel()"
            :show-dropdown-arrow="false"
            :button-class="chipClass + (removable ? ' chip--removable' : '') + (hasValue ? ' chip--has-value' : '')"
            :show-form-footer="true"
            :removable="removable"
            :clearable="isClearable"
            @popover-change="onPopoverChange"
            @clear="onPopoverClear"
            @apply="onPopoverApply"
            @remove-click="onRemove"
            @clear-click="onClear"
        >
            <template slot="content">
                <div class="meta-referral-composite-container">
                    <div
                        v-for="(row, index) in rows"
                        :key="index"
                        class="meta-referral-composite-row"
                    >
                        <div class="meta-referral-composite-row-grid">
                            <div class="meta-referral-composite-field">
                                <label class="meta-referral-composite-label">
                                    {{ $t("chat.filtersSection.type") }}
                                </label>
                                <select v-model="row.selectValue" class="meta-referral-composite-select">
                                    <option value="">{{ $t("auto.select") }}</option>
                                    <option
                                        v-for="option in fieldOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>
                            <div class="meta-referral-composite-field">
                                <label class="meta-referral-composite-label">
                                    {{ $t("chat.filterChip.operator") }}
                                </label>
                                <select v-model="row.operator" class="meta-referral-composite-select">
                                    <option
                                        v-for="option in operatorOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="meta-referral-composite-row-bottom">
                            <div class="meta-referral-composite-field meta-referral-composite-field--value">
                                <label class="meta-referral-composite-label">
                                    {{ $t("chat.filterChip.value") }}
                                </label>
                                <input
                                    type="text"
                                    v-model="row.inputValue"
                                    :placeholder="$t('chat.filterChip.enterValue')"
                                    class="meta-referral-composite-input"
                                />
                            </div>
                            <button
                                type="button"
                                class="meta-referral-composite-remove"
                                :disabled="rows.length <= 1"
                                @click="removeRow(index)"
                            >
                                <i class="material-icons">close</i>
                            </button>
                        </div>
                    </div>

                    <button type="button" class="meta-referral-composite-add" @click="addRow">
                        <i class="material-icons">add</i>
                        <span>{{ $t("chat.filtersSection.addCondition") }}</span>
                    </button>
                </div>
            </template>
        </CustomPopover>
    </div>
</template>

<script>
import CustomPopover from "./CustomPopover.vue";

export default {
    name: "MetaReferralCompositeFilterChip",
    components: {
        CustomPopover,
    },
    props: {
        value: {
            type: Array,
            default: function () {
                return [];
            },
        },
        filterName: {
            type: String,
            default: "",
        },
        icon: {
            type: String,
            default: "ads_click",
        },
        removable: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        dataFilterField: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            rows: [this.createEmptyRow()],
        };
    },
    computed: {
        chipClass() {
            return "chip filter-chip";
        },
        fieldOptions() {
            return [
                { label: this.$t("chat.filtersSection.sourceType"), value: "sourceType" },
                { label: this.$t("chat.filtersSection.sourceId"), value: "sourceId" },
                { label: this.$t("chat.filtersSection.sourceLink"), value: "sourceLink" },
                { label: this.$t("chat.filtersSection.headline"), value: "headline" },
                { label: this.$t("chat.filtersSection.body"), value: "body" },
            ];
        },
        operatorOptions() {
            return [
                { label: this.$t("chat.filterChip.contains"), value: "contains" },
                { label: this.$t("chat.filterChip.notContains"), value: "not_contains" },
                { label: this.$t("chat.filterChip.equals"), value: "equals" },
                { label: this.$t("chat.filterChip.notEquals"), value: "not_equals" },
            ];
        },
        validRows() {
            return this.rows.filter(
                (r) => r.selectValue && r.inputValue !== null && r.inputValue !== undefined && r.inputValue !== ""
            );
        },
        hasValue() {
            return this.validRows.length > 0;
        },
        isClearable() {
            return this.clearable && this.hasValue;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                this.syncRowsFromValue(newVal);
            },
        },
    },
    methods: {
        createEmptyRow() {
            return { selectValue: "", operator: "contains", inputValue: "" };
        },
        syncRowsFromValue(val) {
            if (Array.isArray(val) && val.length > 0) {
                this.rows = val.map((item) => ({
                    selectValue: (item && item.selectValue) || "",
                    operator: (item && item.operator) || "contains",
                    inputValue: (item && item.inputValue) || "",
                }));
            } else {
                this.rows = [this.createEmptyRow()];
            }
        },
        addRow() {
            this.rows.push(this.createEmptyRow());
        },
        removeRow(index) {
            if (this.rows.length <= 1) {
                this.rows = [this.createEmptyRow()];
                return;
            }
            this.rows.splice(index, 1);
        },
        getLabel() {
            const valid = this.validRows;
            if (valid.length === 0) {
                return this.filterName;
            }
            const first = valid[0];
            const fieldMap = this.fieldOptions.reduce((acc, opt) => {
                acc[opt.value] = opt.label;
                return acc;
            }, {});
            const opMap = this.operatorOptions.reduce((acc, opt) => {
                acc[opt.value] = opt.label;
                return acc;
            }, {});
            const typeLabel = fieldMap[first.selectValue] || first.selectValue;
            const opLabel = opMap[first.operator] || opMap.contains;
            const firstText = typeLabel + " " + opLabel + " " + first.inputValue;
            const valueText = valid.length > 1 ? firstText + " (+" + (valid.length - 1) + ")" : firstText;
            return this.filterName ? this.filterName + ": " + valueText : valueText;
        },
        onPopoverChange(visible) {
            if (visible) {
                this.syncRowsFromValue(this.value);
                if (this.rows.length === 0) {
                    this.rows = [this.createEmptyRow()];
                }
            }
        },
        onPopoverClear() {
            this.rows = [this.createEmptyRow()];
            this.$emit("change", null);
        },
        onPopoverApply() {
            const valid = this.validRows.map((r) => ({
                selectValue: r.selectValue,
                operator: r.operator || "contains",
                inputValue: r.inputValue,
            }));
            if (valid.length > 0) {
                this.$emit("change", valid);
            } else {
                this.$emit("change", null);
            }
        },
        onRemove() {
            this.$emit("remove");
        },
        onClear() {
            this.onPopoverClear();
        },
        openPopover() {
            if (this.$refs.customPopover) {
                this.$refs.customPopover.openPopover();
            }
        },
        closePopover() {
            if (this.$refs.customPopover) {
                this.$refs.customPopover.closePopover();
            }
        },
    },
};
</script>

<style lang="scss">
.meta-referral-composite-container {
    padding: 16px;
    min-width: 340px;
    max-height: 400px;
    overflow-y: auto;
}

.meta-referral-composite-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    margin-bottom: 10px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;

    &:last-of-type {
        margin-bottom: 12px;
    }
}

.meta-referral-composite-row-grid {
    display: flex;
    gap: 8px;
}

.meta-referral-composite-row-bottom {
    display: flex;
    align-items: flex-end;
    gap: 8px;
}

.meta-referral-composite-field {
    flex: 1;
    min-width: 0;

    &--value {
        flex: 1;
    }
}

.meta-referral-composite-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.meta-referral-composite-select,
.meta-referral-composite-input {
    width: 100%;
    padding: 7px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 13px;
    background: #fff;
    color: #374151;
    outline: none;

    &:focus {
        border-color: #22272d;
        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
    }
}

.meta-referral-composite-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 32px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #fff;
    color: #6b7280;
    cursor: pointer;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background: #fee2e2;
        border-color: #fca5a5;
        color: #b91c1c;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .material-icons {
        font-size: 16px;
    }
}

.meta-referral-composite-add {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border: 1px dashed #9ca3af;
    border-radius: 4px;
    background: #fff;
    color: #374151;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #f3f4f6;
        border-color: #22272d;
        color: #22272d;
    }

    .material-icons {
        font-size: 16px;
    }
}
</style>
